// Finished games, campaigns and usernames on PocketBase: the app's side of
// docs/pocketbase-migration-plan.md. net.js decides when to use it (src/migration_flags.js).
//
// A factory rather than module state so it can be tested in node against a local
// PocketBase: createStatsStore(pb, storage) where storage has idb-keyval's get/set/getMany/
// setMany shape.
//
// Read side: the Stats list loads only the small `summary` of each game, from an IndexedDB
// copy first and then only what changed on the server since. Cards, prefs and per-turn
// series (ensureDetails) and the log/replay blobs (loadBlobs) are fetched when a view needs
// them. Finished games don't change, so details are cached against the game's docHash.
import split from '../pb_hooks/stcc_split.js';

const LIST_CACHE = 'stcc_pb_games_v1';
const DETAILS_CACHE = 'stcc_pb_details_v1:';
const OVERLAP_MS = 60 * 1000; // re-read a minute before the last sync, for out-of-order commits
const DETAILS_CHUNK = 25;     // keys per request; PocketBase caps a filter at 3500 chars
const LIST_FIELDS = 'id,key,summary,boxRefs,hidden,docHash,fullStateSha256,replayInfoSha256,updated';

// Game objects handed to Stats.svelte look exactly like the Firestore documents, minus the
// heavy fields until ensureDetails() adds them. What PocketBase knows about them rides along
// as non-enumerable properties, so it never shows up in an export or a re-upload.
function hidden(o, name, value){
    Object.defineProperty(o, name, {value, enumerable: false, writable: true, configurable: true});
}

export function toGame(row){
    let g = {...row.summary};
    hidden(g, '_pb', {
        docHash: row.docHash,
        fullStateSha256: row.fullStateSha256 || '',
        replayInfoSha256: row.replayInfoSha256 || '',
        detailsLoaded: false,
    });
    hidden(g, 'boxRefs', row.boxRefs || []);
    return g;
}

function pbDate(s){
    // PocketBase dates look like "2026-09-26 08:16:23.123Z"
    return new Date(String(s).replace(' ', 'T'));
}

async function sha256Hex(bytes){
    let digest = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
}

export function createStatsStore(pb, storage, {log = console.log} = {}){
    const safe = async (f, fallback) => {
        // IndexedDB can be missing or full (private windows, quota); the cache is only a cache.
        try{
            return await f();
        }catch(e){
            log('stats cache unavailable:', e && e.message);
            return fallback;
        }
    };

    // ---- list

    async function downloadGames(force = false){
        let cache = force ? null : await safe(() => storage.get(LIST_CACHE), null);
        let rows = new Map(cache && cache.rows ? cache.rows.map(r => [r.key, r]) : []);
        let options = {fields: LIST_FIELDS, batch: 1000, requestKey: null};
        if(cache && cache.lastSynced){
            options.filter = pb.filter('updated >= {:since}', {since: new Date(pbDate(cache.lastSynced).getTime() - OVERLAP_MS)});
        }
        let fresh = await pb.collection('stcc_games').getFullList(options);
        let lastSynced = cache && cache.lastSynced || null;
        for(let r of fresh){
            rows.set(r.key, r);
            if(!lastSynced || pbDate(r.updated) > pbDate(lastSynced)) lastSynced = r.updated;
        }
        let all = [...rows.values()];
        await safe(() => storage.set(LIST_CACHE, {lastSynced, rows: all}));
        return all.filter(r => !r.hidden).map(toGame);
    }

    async function downloadGame(key){
        let row = await pb.collection('stcc_games').getFirstListItem(pb.filter('key = {:key}', {key}), {fields: LIST_FIELDS, requestKey: null});
        let g = toGame(row);
        await ensureDetails([g]);
        return g;
    }

    // ---- details: cards, prefs, per-turn series

    function applyDetails(g, d){
        let doc = split.reassemble({summary: g, details: d});
        for(let k of split.HEAVY){
            if(k in doc) g[k] = doc[k];
        }
        g.players = doc.players;
        g._pb.detailsLoaded = true;
    }

    // Returns true straight away when there is nothing to fetch, so {#await} doesn't flash.
    // Svelte re-evaluates an {#await} whenever the list is touched, so a game already being
    // fetched joins that fetch rather than starting another.
    function ensureDetails(games){
        let need = (games || []).filter(g => g && g._pb && !g._pb.detailsLoaded);
        if(need.length == 0) return true;
        let fresh = need.filter(g => !g._pb.loading);
        if(fresh.length){
            let p = loadDetails(fresh).finally(() => { for(let g of fresh) g._pb.loading = null; });
            for(let g of fresh) g._pb.loading = p;
        }
        return Promise.all([...new Set(need.map(g => g._pb.loading))]).then(() => true);
    }

    async function loadDetails(need){
        let cached = await safe(() => storage.getMany(need.map(g => DETAILS_CACHE + g.key)), []);
        let missing = [];
        need.forEach((g, i) => {
            let c = cached && cached[i];
            if(c && c.docHash == g._pb.docHash) applyDetails(g, c);
            else missing.push(g);
        });
        let chunks = [];
        for(let i = 0; i < missing.length; i += DETAILS_CHUNK) chunks.push(missing.slice(i, i + DETAILS_CHUNK));
        let fetchChunk = async (chunk) => {
            let params = {};
            let filter = chunk.map((g, i) => { params[`k${i}`] = g.key; return `key = {:k${i}}`; }).join(' || ');
            let rows = await pb.collection('stcc_game_details').getFullList({
                filter: pb.filter(filter, params), fields: 'key,cards,prefs,turns,missing', batch: DETAILS_CHUNK, requestKey: null
            });
            let byKey = new Map(rows.map(r => [r.key, r]));
            let toStore = [];
            for(let g of chunk){
                let r = byKey.get(g.key);
                if(!r){
                    log(`no details on the server for ${g.key}`);
                    g.cards = g.cards || [];
                    g._pb.detailsLoaded = true;
                    continue;
                }
                let d = {docHash: g._pb.docHash, cards: r.cards, prefs: r.prefs, turns: r.turns || {}, missing: r.missing || []};
                applyDetails(g, d);
                toStore.push([DETAILS_CACHE + g.key, d]);
            }
            await safe(() => storage.setMany(toStore));
        };
        // A few requests at a time: quick on a phone, gentle on the server.
        let queue = chunks.slice();
        let worker = async () => { for(let c; (c = queue.shift());) await fetchChunk(c); };
        await Promise.all([worker(), worker(), worker()]);
        return true;
    }

    // ---- blobs: the compressed full state and replay

    function hasBlobs(g){
        return !!(g && g._pb && g._pb.fullStateSha256);
    }

    async function loadBlobs(g){
        for(let kind of split.BLOBS){
            let sha = g._pb && g._pb[`${kind}Sha256`];
            if(!sha) continue;
            let rec = await pb.collection('stcc_game_blobs').getFirstListItem(
                pb.filter('key = {:key} && kind = {:kind} && sha256 = {:sha}', {key: g.key, kind, sha}), {requestKey: null});
            let res = await fetch(pb.files.getURL(rec, rec.file));
            if(!res.ok) throw new Error(`${kind} download failed: ${res.status}`);
            let bytes = new Uint8Array(await res.arrayBuffer());
            if(await sha256Hex(bytes) != sha) throw new Error(`${kind} download is corrupt`);
            g[kind] = bytes;
        }
        return !!g.fullState;
    }

    // ---- writes (through pb_hooks/stcc_upload.pb.js; plain collection writes are refused)

    // Resolves true only when PocketBase has confirmed the whole game: the document and
    // every blob we sent, by hash.
    async function uploadGame(game, key){
        let doc = {};
        for(let k of Object.keys(game)){
            if(!split.BLOBS.includes(k)) doc[k] = game[k];
        }
        doc.key = key;
        let form = new FormData();
        form.append('doc', JSON.stringify(doc));
        let sent = {};
        for(let kind of split.BLOBS){
            let bytes = split.blobBytes(game[kind]);
            if(!bytes) continue;
            sent[kind] = await sha256Hex(bytes);
            form.append(kind, new Blob([bytes]), `${kind}.bin`);
            form.append(`${kind}Sha256`, sent[kind]);
        }
        let r = await pb.send('/api/stcc/games', {method: 'POST', body: form, requestKey: null});
        if(!['created', 'updated', 'unchanged', 'filled'].includes(r.status)) throw new Error(`unexpected upload result ${r.status}`);
        for(let kind of Object.keys(sent)){
            if(r[`${kind}Sha256`] != sent[kind]) throw new Error(`server did not keep the ${kind} that was sent`);
        }
        return true;
    }

    async function uploadCampaign(campaign){
        let r = await pb.send('/api/stcc/campaigns', {method: 'POST', body: JSON.stringify({campaign}), headers: {'content-type': 'application/json'}, requestKey: null});
        return ['created', 'updated', 'unchanged'].includes(r.status);
    }

    async function setUsername(uid, name){
        let existing = null;
        try{
            existing = await pb.collection('stcc_usernames').getFirstListItem(pb.filter('uid = {:uid}', {uid}), {requestKey: null});
        }catch(e){
            if(e.status != 404) throw e;
        }
        if(!existing) await pb.collection('stcc_usernames').create({uid, name}, {requestKey: null});
        else if(existing.name != name) await pb.collection('stcc_usernames').update(existing.id, {name}, {requestKey: null});
        return true;
    }

    // ---- campaigns

    async function loadCampaigns(username, onlyOngoing = false){
        let rows = await pb.collection('stcc_campaigns').getFullList({filter: pb.filter('username = {:u}', {u: username}), fields: 'data', requestKey: null});
        let list = rows.map(r => r.data);
        // Firestore's where('ended', '==', false) skipped campaigns with no `ended` at all
        return onlyOngoing ? list.filter(c => c.ended === false) : list;
    }

    async function loadAllCampaigns(){
        let rows = await pb.collection('stcc_campaigns').getFullList({fields: 'data', batch: 1000, requestKey: null});
        return rows.map(r => r.data);
    }

    return {downloadGames, downloadGame, ensureDetails, hasBlobs, loadBlobs, uploadGame, uploadCampaign, setUsername, loadCampaigns, loadAllCampaigns};
}
